import styled, { css } from 'styled-components';

import { UserPlaceholderImg } from '../../../assets';
import { IconButton } from '../../../components';
import { Button } from '../../../components';
import { bottomNavHeight } from '../../../components/layout/BottomNav/BottomNav.styles';
import { SCROLLABLE_CARD_MIN_WIDTH } from '../../../components/VehicleCard/VehicleBoxesAndActions/VehicleBoxAndActions.styles';
import ZIndexLayers from '../../../services/ZIndexLayers';
import { ThemeProp } from '../../../theme/Themes';
import { setSVGChildColors } from '../../../utils/Listings';

export const VEHICLE_INFO_COLUMN_WIDTH = '17.5rem';
export const DESKTOP_RIGHT_PADDING = '2.5rem';

export const ListingTitle = styled.div`
  font-size: ${(props) => props.theme.fontSize.l};
  line-height: ${(props) => props.theme.lineHeight.xl};
  text-overflow: ellipsis;
  overflow: hidden;
  margin-bottom: 1rem;
`;

export const StyledButton = styled(Button)`
  && {
    width: 140px;
  }
`;

export const VehicleCardContainer = styled.div`
  z-index: ${ZIndexLayers.elevateOne};
  cursor: pointer;
  // If it is the last element, add bottom padding
  &:last-child {
    padding-bottom: 0.5rem;
  }

  /* Show checkbox on hover when no selections */
  .table-container:not(.has-selected-listings) &:hover {
    .checkbox-container {
      visibility: visible;
    }
  }
`;

export const VehicleCardRightSideContainer = styled(VehicleCardContainer)`
  flex: 1;
  margin-right: 2.25rem;
`;

export const Container = styled.div`
  width: 100%;
  height: 100%;
  height: calc(100vh - 10.5rem); /* This adds the space between cards and bottom of the screen */
  overflow-y: auto;
  overflow-x: hidden;
  z-index: ${ZIndexLayers.fixedElement};

  /* Calculate and store scrollbar width as CSS variable */
  --scrollbar-width: calc(100vw - 100%);

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    /* 16rem = Height of views on top of cards (12rem) + height of bottom nav (4rem) */
    height: ${`calc(100dvh - 12rem - ${bottomNavHeight})`};
    padding: 0 1rem;
  }
`;

export const AnswerColorPreview = styled.div<any>`
  background: ${(props) => props.color || 'transparent'};
  width: 0.875rem;
  height: 0.875rem;
  border-radius: 0.5rem;
`;

export interface HeaderIconButtonProps {
  $focused?: boolean;
}

export const headerIconSVGStrokePartial = css`
  svg,
  path,
  circle,
  rect {
    stroke: ${({ theme }: ThemeProp) => theme.colors.greyscale.c100};
  }
`;

export const LISTINGS_ICON_BUTTON_SIZE = '2rem';

export const ListingsIconButton = styled(IconButton)<HeaderIconButtonProps>`
  margin: 0;
  padding: 0;
  width: ${LISTINGS_ICON_BUTTON_SIZE};
  height: ${LISTINGS_ICON_BUTTON_SIZE};
  cursor: pointer;
  border-radius: 50%;
  box-shadow: 0px 1px 4px 0px rgba(41, 12, 55, 0.1); // NOTE: Spec from design system.
  background: ${(props: ThemeProp & HeaderIconButtonProps) =>
    props.$focused ? props.theme.colors.primary.c800 : props.theme.colors.greyscale.c900};

  &:hover {
    background: ${(props) => props.theme.colors.primary.c800};
    ${(props: ThemeProp) => setSVGChildColors(props.theme.colors.primary.c200)}
  }
  ${headerIconSVGStrokePartial}
`;

export const HorizontalScrollDiv = styled.div`
  min-width: calc(${SCROLLABLE_CARD_MIN_WIDTH} + ${DESKTOP_RIGHT_PADDING});
  height: 0.625rem;
`;

export const UserAvatarIcon = styled(UserPlaceholderImg)`
  border-radius: 50%;
  overflow: hidden;
`;

export const LISTING_Z_INDEXES = {
  headerContainer: ZIndexLayers.elevateOne + 1,
  selectionBar: ZIndexLayers.elevateOne + 2,
  scrollContent: ZIndexLayers.elevateOne + 3,
};

export const HeaderContainer = styled.div`
  display: flex;
  position: sticky;
  top: 0;
  z-index: ${LISTING_Z_INDEXES.headerContainer};
  padding: 0.5rem 0;
  background: #fbf9ff;
  width: 100%;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const gradientOverlayCommonStyles = css`
  content: '';
  position: absolute;
  top: 0;
  width: 2.3rem;
  height: 2rem;
  flex-shrink: 0;
  z-index: ${ZIndexLayers.overlay};
`;

export interface FixedColumnHeaderProps {
  $disablePadding?: boolean;
}

export const FixedColumnHeader = styled.div<FixedColumnHeaderProps>`
  position: relative;
  width: ${VEHICLE_INFO_COLUMN_WIDTH};
  min-width: ${VEHICLE_INFO_COLUMN_WIDTH};
  padding-left: ${(props) => (props.$disablePadding ? '0' : '1.25rem')};
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.m};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};

  &::after {
    display: flex;
    ${gradientOverlayCommonStyles};
    background: linear-gradient(270deg, #fbf9ff 25%, rgba(251, 249, 255, 0) 100%); // NOTE: Spec from design system.
    left: 16.7rem;
    transform: rotate(180deg);
  }
`;

/*
TODO: Find a way to measure the scrollbar width
This issue makes the sort component not aligned with cards in some cases
And affects also the selection bar alignments with the cards too
*/
export const SCROLL_WIDTH = '0.75rem';

export const ScrollableHeaderWrapper = styled.div`
  width: calc(100% - ${VEHICLE_INFO_COLUMN_WIDTH} - ${DESKTOP_RIGHT_PADDING} - ${SCROLL_WIDTH});
  overflow-x: hidden;
`;

export const ScrollableColumnHeader = styled.div`
  display: flex;
  gap: 2.25rem;
  min-width: ${SCROLLABLE_CARD_MIN_WIDTH};
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.m};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
  padding-left: 1.25rem;

  overflow-x: auto;
  background: #fbf9ff;
  /* Hide scrollbar but keep functionality */
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;
